<!--
业务变更说明：
- 新增初始化流程，根据checkout-flow.md文档实现
- 实现从URL参数中获取订单信息或通过API获取订单信息
- 根据订单状态进行页面跳转：失败状态跳转失败页面，Shopify订单跳转新收银台，无支付国家跳转国家选择页面

Business Change Description:
- Added initialization process according to checkout-flow.md document
- Implemented getting order information from URL parameters or through API
- Page navigation based on order status: failed status redirects to failure page, Shopify order redirects to new checkout, no payment country redirects to country selection page
-->
<template>
  <div :style="{ '--brand-color': app.brandInfo?.color || '#503df6' }">
    <!-- 使用分割布局组件,并添加全屏加载效果 -->
    <split-layout>
      <!-- 左侧内容区域 -->
      <template #left>
        <!-- 订单信息组件 -->
        <OrderInfo :order="order" v-if="!isMobile" />
      </template>
      <!-- 右侧内容区域 -->
      <template #right>
        <div v-if="order" class="right-container">
          <!-- 吸顶 -->
          <div class="sticky-top">
            <OnCountdownTimer style="margin-bottom: 24px;" :createTime="order.createTime"
              :effectiveMinutes="order.effectiveMinutes" />
            <OnErrorMsgPanel ref="errorPanel" style="margin-bottom: 24px;" :visible="retryInfo.visible"
              :errorText="retryInfo.retryAdvice" @onClose="retryInfo.visible = false" />
          </div>

          <OrderInfo :order="order" v-if="isMobile"/>
          <PaymentForm ref="paymentFormRef" :order="order" @submit="submit" @country-change="handleCountryChange"
            @cardNum-validate="handleCardNumValidate" :formLoading="formLoading" />
        </div>

      </template>
    </split-layout>
    <!-- 成功状态 ,TODO billingAddress直接取order数据行不行,redirectUrl取order.data.redirectUrl是否可以-->
    <Modal :modelValue="currentStatus === CHECKOUT_STATUS.SUCCESS"
      :title="this.$t('message.result.success')"
      :description="modalMessage || this.$t('message.result.successPop')"
      :status="'success'"
      :buttonText="this.$t('message.checkout.returnMerchant')"
      :redirectUrl="redirectUrl || (order?.returnUrl || (order?.data && order?.data.redirectUrl))"
      :billingAddress="order && !order.hideBillingInformation ? order.billingAddress : ''"
    />
    <!-- 失败状态 -->
    <Modal :modelValue="currentStatus === CHECKOUT_STATUS.FAILED"
      :title="this.$t('message.result.failed')"
      :description="modalMessage ? `${this.$t('message.result.reason')}: ${modalMessage}` : ''"
      :status="'error'"
      :buttonText="this.$t('message.checkout.returnMerchant')"
      :redirectUrl="redirectUrl || (order?.returnUrl || (order?.data && order?.data.redirectUrl))"
    />
    <!-- Loading -->
    <LoadingArrow v-if="currentStatus === CHECKOUT_STATUS.LOADING" />
    <!-- 提交中 -->
    <LoadingArrow
      v-if="currentStatus === CHECKOUT_STATUS.SUBMITTING || currentStatus === CHECKOUT_STATUS.PENDING"
      :text="this.$t('message.result.processing')"
    />
    <!-- 错误 -->
    <ErrorPage v-if="currentStatus === CHECKOUT_STATUS.ERROR" />
  </div>
</template>

<script>
// 导入订单信息组件
import { mapGetters } from 'vuex';

import OnCountdownTimer from '@/componentsV2/busi/countdown-timer/index.vue';
import ErrorPage from '@/componentsV2/busi/Error/Error.vue';
import OnErrorMsgPanel from '@/componentsV2/busi/errorMsg-panel/index.vue';
import LoadingArrow from '@/componentsV2/busi/Loading/LoadingArrow.vue';
import Modal from '@/componentsV2/busi/Modal/Modal.vue';
import OrderInfo from '@/componentsV2/busi/OrderInfo/OrderInfo.vue';
import PaymentForm from '@/componentsV2/busi/PaymentForm/PaymentForm.vue';
import SplitLayout from '@/componentsV2/busi/SplitLayout.vue';
import { deviceFingerprintManager as sendEventInstance } from '@/plugins/eventTrackManager/deviceFingerprintManager';
import { scrollToTop } from '@/utils/tools/utils';

import { initCheckoutProcess, rerenderCheckout, getOrderStatus } from './bus/initCheckout';
import { initFraudPrevention } from './bus/initFraudPrevention';
import { submitPayment } from './bus/submitPayment';
import { CHECKOUT_STATUS } from './config';

// 导入分割布局组件
// 导入初始化结账流程函数
// 导入防欺诈初始化函数
export default {
  // 组件名称
  name: 'CheckoutV3',
  // 注册子组件
  components: {
    SplitLayout,
    OrderInfo,
    Modal,
    LoadingArrow,
    ErrorPage,
    PaymentForm,
    OnErrorMsgPanel,
    OnCountdownTimer,
  },
  // 组件数据
  data() {
    return {
      CHECKOUT_STATUS,
      // 当前状态
      currentStatus: CHECKOUT_STATUS.LOADING,
      // 全屏加载状态
      // fullscreenLoading: true,
      // 是否为移动端，依据window.innerWidth<=991
      isMobile: window.innerWidth <= 991,
      // 订单信息对象
      order: null,
      // 从URL获取订单key
      key: this.$route.query.key ? this.$route.query.key : this.$route.params.id,
      // 请求时间戳
      requestingTime: 0,
      // 字段配置名称列表
      fieldConfigurationNames: [],
      // 分期信息
      instalmentInfo: null,
      // 语言
      lang: (this.$i18n && this.$i18n.locale) || 'en',
      // 卡号焦点时间
      cardNumFocusTime: null,
      // 重试信息
      retryInfo: {
        visible: false,
        retry: undefined,
        retryAdvice: undefined,
      },
      oldCardNum: '',
      modalMessage: '',
      redirectUrl: '',
      formLoading: false,

    };
  },
  computed: {
    ...mapGetters(['app']),
  },
  // 组件创建时的生命周期钩子
  created() {
    // 设置浏览器标题
    document.title = this.$t('message.title');
    // 移动端判断及监听
    this.handleResize = () => {
      this.isMobile = window.innerWidth <= 991;
    };
    window.addEventListener('resize', this.handleResize);
    // 调用初始化结账流程
    const initCheckout = async () => {
      try {
        this.setCheckoutStatus(CHECKOUT_STATUS.LOADING);
        const orderInfo = await initCheckoutProcess(
          {
            route: this.$route,
            key: this.key,
            requestingTime: this.requestingTime,
          },
          null, null,
          (params) => {
            this.sendBehaviorEvent(params);
          },
        );
        if (!orderInfo) {
          return;
        }
        this.order = orderInfo;

        this.setRetry(orderInfo);
        this.$store.dispatch('app/setCards', orderInfo.cardTypeInfo);
        this.$store.dispatch('app/setBrandInfo', { ...(orderInfo.brandConfig || {}), website: orderInfo.website });
        this.setCheckoutStatus(CHECKOUT_STATUS.FILLING);
      } catch (state) {
        this.setCheckoutStatus(state.status, state.message, state.others);
      }
    };
    initCheckout();
  },
  // 组件创建前的生命周期钩子
  beforeCreate() {
    // 初始化防欺诈功能
    initFraudPrevention(this.$route.query.key ? this.$route.query.key : this.$route.params.id);
  },
  // 组件销毁前的生命周期钩子
  beforeDestroy() {
    // 移除resize监听，防止内存泄漏
    window.removeEventListener('resize', this.handleResize);
    this.sendBehaviorEvent({ e: 'exit_checkout' });
  },
  // 组件方法
  methods: {
    scrollToTop,
    /**
     * 修改结账状态
     * @param {string} status - 状态值
     * @param {string} message - 错误信息
     */
    setCheckoutStatus(status, message, others) {
      if (Object.values(CHECKOUT_STATUS).includes(status)) {
        this.currentStatus = status;
        this.modalMessage = message;
        this.redirectUrl = others?.redirectUrl;
        // 如果是pending状态，则调用轮询订单状态.
        if (status === CHECKOUT_STATUS.PENDING) {
          getOrderStatus({ key: this.key }).catch((error) => {
            this.setCheckoutStatus(error.status, error.message, error.others);
          });
        }
      } else {
        this.currentStatus = CHECKOUT_STATUS.ERROR;
        console.error('Invalid checkout status:', status);
      }
    },
    /**
     * 统一处理收银台重新渲染
     * @param {Object} options - 重新渲染所需的参数
     * @param {string} options.country - 国家代码
     * @param {string} options.cardNum - 卡号
     */
    async handleRerenderCheckout(options = {}) {
      const { country, cardNum } = options;

      try {
        // 设置加载状态
        this.formLoading = true;

        // 防止重复请求 - 如果卡号没变且是卡号验证触发的，则不请求
        if (cardNum && cardNum === this.oldCardNum && !country) {
          this.formLoading = false;
          return;
        }

        // 保存当前卡号，用于后续比较
        if (cardNum) {
          this.oldCardNum = cardNum;
        }

        // 调用重新渲染逻辑
        await rerenderCheckout(
          { key: this.key },
          this.order,
          // 更新订单信息回调函数
          (orderInfo) => {
            this.order = orderInfo;
            // this.setRetry(orderInfo);
            this.$store.dispatch('app/setCards', orderInfo.cardTypeInfo);
            this.$store.dispatch('app/setBrandInfo', {
              ...(orderInfo.brandConfig || {}),
              website: orderInfo.website,
            });
          },
          // 设置加载状态回调函数
          (loading) => {
            this.formLoading = loading;
          },
          {
            country: country || this.order.paymentCountry,
            cardNumber: cardNum || '',
          },
        );
      } catch (error) {
        console.error('重新渲染收银台失败:', error);
        // 已确认，getFieldConfig_checkout该接口不对异常进行处理
        // this.setCheckoutStatus(CHECKOUT_STATUS.ERROR, error.message, error.others);
      } finally {
        // 确保无论成功失败都关闭加载状态
        this.formLoading = false;
      }
    },
    handleCountryChange({ country, cardNum }) {
      this.handleRerenderCheckout({
        country: country.code,
        cardNum,
      });
    },
    handleCardNumValidate(cardNum) {
      this.handleRerenderCheckout({ cardNum });
    },
    /**
     * 提交支付
     */
    async submit(data) {
      // 使用抽离的提交方法
      // this.currentStatus = CHECKOUT_STATUS.SUBMITTING;
      try {
        await submitPayment({
          formRef: this.$refs.formRef,
          form: data,
          fieldConfigurationNames: this.fieldConfigurationNames,
          instalmentInfo: this.instalmentInfo,
          key: this.key,
          router: this.$router,
          order: this.order,
          setFullscreenLoading: (loading) => {
            this.$refs.paymentFormRef.setSubmitLoading(loading);
          },
          gaOptions: {
            key: this.key,
            requestingTime: this.requestingTime,
            cardNumFocusTime: this.cardNumFocusTime,
            lang: this.lang,
          },
          onRetry: (data) => {
            this.setRetry(data);
          },
          trackingCallBack: (params) => {
            this.sendBehaviorEvent(params);
          },

        });
      } catch (error) {
        console.log('-------------------------', error.status, error.message, error.others);
        this.setCheckoutStatus(error.status, error.message, error.others);
      }
    },

    /**
     * 处理重试
     */
    setRetry(data) {
      // 更新订单信息
      this.order = { ...this.order, ...data };
      // 更新重试信息
      this.retryInfo.retry = data.retry;
      this.retryInfo.retryAdvice = data.retryAdvice;
      this.retryInfo.visible = data.retry === '1';
      if (data.retry === '1') {
        this.scrollToTop();
        this.$nextTick(() => {
          // 通过PaymentForm组件的动态表单引用清空CVV
          if (this.$refs.paymentFormRef && this.$refs.paymentFormRef.$refs.dynamicFormRefPayment) {
            this.$refs.paymentFormRef.$refs.dynamicFormRefPayment.setFormData({ cvv: '' });
          }
        });
        this.setCheckoutStatus(CHECKOUT_STATUS.FILLING, '', data);
      }
    },
    /**
     * 订单结束后跳转到失败页
     */
    // handleTimeEnd() {
    //   this.setCheckoutStatus(CHECKOUT_STATUS.FAILED, this.$t('message.error.paymentLinkExpired'), {
    //     redirectUrl: this.order.redirectUrl || undefined,
    //   });
    // },

    /**
     * 发送埋点
     */
    sendBehaviorEvent(params) {
      const param = {
        m: this.order?.merchantTxnId || '',
        kd: this.order?.merchantNo || '',
        re: this.retryInfo?.retry || '',
        k: this.$route.query?.key || '',
        ct: 'CHECKOUT',
        co: this.order?.txnCurrency || '',
        at: this.order?.txnAmount || '',
        gwq: this.order?.txnId || '',
        bh: this.order?.paymentCountry || '',
        ...params,
      };
      sendEventInstance.send(param);
    },
  },
};
</script>

<style lang="scss" scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: #fff;
  width: 100%;
}
.right-container {
  max-width: 406px;
}
</style>
